"use client";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useEnterSubmit } from "@/hooks/use-enter-submit";
import { SendIcon } from "@/assets/svg-icons/send-icon";
import { AiThinking } from "../elements/message-varients";
import { Chat, Message, MessageResponse } from "@/types/chat";
import {
  AGENTS,
  AGENT_DESCRIPTIONS,
  buildChatPath,
  CUSTOM_SCROLLBAR,
  MAX_CHAT_QUERY_LENGTH,
  MESSAGE_SHELL,
  REFETCH_INTERVAL,
  THREAD_BUSY,
  WENT_WRONG,
} from "@/lib/constants";
import useGetChatService from "@/service-hooks/use-get-chat-service";
import useSaveMessageService from "@/service-hooks/use-save-message-service";
import usePollChatService from "@/service-hooks/use-poll-chat-service";
import { DiagramComponent } from "@/types/diagram";
import MessageLayout from "./message-layout";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  X,
  Layers,
  Loader2,
  Lightbulb,
  CirclePlus,
  Square,
} from "lucide-react";
import { useChat } from "@/context/chat-context";
import { usePanelProps } from "@/context/panel-props-context";
import { usePanel } from "@/context/panel-context";
import BreadCrumb from "./breadcrumb-layout";
import { cn, parseServerUtcMs, stripBreezeAiFences } from "@/lib/utils";
import { escapeChatQuery } from "@/lib/sanitize-chat-query";
import RaiseJiraTicketDialog from "../elements/raise-jira-ticket-dialog";
import {
  useListJiraBoards,
  type ChatTranscriptTurn,
} from "@/service-hooks/use-jira-issue-service";
import { getGitDocumentById } from "@/service-hooks/use-get-git-document-service";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useQueryClient } from "@tanstack/react-query";
import { getDocumentOntologyStatus } from "@/lib/ontology-status";
import useStartImpactAnalysisRun, {
} from "@/service-hooks/use-impact-analysis-runs";
import { Document, StatusEnum } from "@/types/document";
import { toast } from "sonner";
import {
  FILE_ACCEPT_ATTR,
  SUPPORTED_FORMATS_SHORT,
  isValidFileType,
} from "@/lib/supported-file-types";
import { useChatInput } from "@/context/chat-input-context";
import ChatModeSelector, {
  CHAT_MODES,
  ChatMode,
} from "../elements/chat-mode-selector";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import ArchitectureAgentLayout from "./architecture-agent-layout";
import { useDiagram } from "@/context/diagram-context";
import { useHistory } from "@/context/history-context";
import equal from "fast-deep-equal";
import ChatWelcomeScreen from "../elements/chat-welcome-screen";
import ArchitectureWelcomeScreen from "../elements/architecture-welcome-screen";
import AttachmentLayout from "./attachment-layout";
import ChatThreadsSidebar from "../elements/chat-threads-sidebar";
import {
  chatThreadsQueryKey,
  useCancelChatRun,
} from "@/service-hooks/use-chat-threads-service";
import useGenerateFlowService from "@/service-hooks/use-generate-flow-service";
import { compileDiagram } from "@/lib/diagram-components.lib";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import { useUI } from "@/context/ui-context";
import { useLibrary } from "@/context/library-context";
import { TOAST_DURATION } from "@/lib/constants";

// How many trailing user/assistant exchanges are sent to the Jira drafting
// agent. The remaining three mirror the server-side caps in
// jira-issue.validation.ts — enforced here too so a long conversation degrades to
// less context instead of being rejected with a 400 the user can do nothing about.
const JIRA_CONTEXT_PAIRS = 3;
const JIRA_MAX_TURNS = 12;
const JIRA_MAX_TURN_CHARS = 8000;
const JIRA_MAX_TOTAL_CHARS = 24000;

const ChatScreen = ({ statusData }: { statusData: Document[] }) => {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthHeaders();
  const { id = "", agent: agentParam, threadId: threadIdParam } = useParams();
  const navigate = useNavigate();
  // The URL is the source of truth for the active thread. Undefined means
  // "no thread yet" — the backend will mint one on the next message and we
  // then push the returned threadId into the URL below.
  const threadId = threadIdParam || undefined;
  const { showAttachmentPoints } = useUI();
  const { handleMissingDependencies } = useLibrary();

  // Default to requirement_agent (CHAT_AGENT) if no agent specified
  const agent = agentParam || AGENTS.CHAT_AGENT;

  const { formRef, onKeyDown } = useEnterSubmit();
  const { messages, setMessages, chatMode, setChatMode } = useChat();
  const { setPanelProps } = usePanelProps();
  const { setPanelType } = usePanel();
  const { chatInput, setChatInput } = useChatInput();

  // General Query (chatMode = "general") pre-dates the threads feature and
  // must keep its original single-stream UX and URL shape (`/chat/{id}`, no
  // `/t/{threadId}` segment). Treat threadId as undefined in this mode
  // everywhere — the send request, the chat history query, the poller, and
  // the post-send navigate — so the backend, n8n, and the router all see
  // exactly what they saw before threads shipped.
  const isGeneralMode = chatMode === ChatMode.GENERAL;
  const effectiveThreadId = isGeneralMode ? undefined : threadId;
  const {
    isDiagramLoading,
    currentDiagramInfo,
    setDiagramComponents: setContextDiagramComponents,
    setIsDiagramLoading,
    canvasSize,
    setComposedSVG,
    selectShape,
  } = useDiagram();
  const history = useHistory();

  // Only fetch chat data when there is actually a conversation to load:
  //   * General mode — pre-threads flow, whole cross-thread stream IS the
  //     conversation, always fetch.
  //   * Threaded mode with a threadId — fetch that thread.
  //   * Threaded mode with NO threadId (the "New Chat" landing) — do NOT
  //     fetch. Without this guard the query falls back to the unfiltered
  //     `/chat/byUUID` endpoint and returns the latest messages ACROSS
  //     threads. If any of those is still `processing`, the resume-polling
  //     effect below flips `isPolling` on for a fresh empty screen, where
  //     `shouldPoll` is false — so the poll never actually runs and never
  //     clears itself, leaving "Breeze.AI processing…" stuck forever.
  const chatFetchEnabled = isGeneralMode || !!threadId;
  const {
    data: chatInfiniteData,
    isLoading,
    error: chatFetchError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetChatService(id, agent, chatFetchEnabled, effectiveThreadId);

  // The URL points at a thread that does not exist (or is not the user's):
  // say so and fall back to a new chat instead of leaving a blank pane.
  useEffect(() => {
    if (
      !threadId ||
      isGeneralMode ||
      (chatFetchError as { status?: number } | null)?.status !== 404
    ) {
      return;
    }
    toast.error("Thread not found. Starting a new chat.");
    navigate(buildChatPath(agent, id), { replace: true });
  }, [chatFetchError, threadId, isGeneralMode, agent, id, navigate]);

  // Flatten all infinite query pages into a single chat array.
  // Pages are fetched newest-first (page 1 = latest), so reverse the page
  // order so older messages appear at the top of the conversation.
  const chatData = useMemo(() => {
    if (!chatInfiniteData?.pages?.length) return undefined;
    const allChats = [...chatInfiniteData.pages]
      .reverse()
      .flatMap((page) => page.data);
    const total = chatInfiniteData.pages[0]?.total ?? 0;
    const canViewRunDetails = chatInfiniteData.pages[0]?.canViewRunDetails ?? false;
    return { data: allChats, total, canViewRunDetails };
  }, [chatInfiniteData]);

  const { mutate, isPending, abort: abortSendMessage } = useSaveMessageService(
    handleOnSendSettled,
  );
  const { mutate: cancelRun } = useCancelChatRun();
  // Functional and architecture both start an impact-agent run on the agents API, as on the ontology pages.
  const { mutate: startGenerationRun } = useStartImpactAnalysisRun();
  const { mutate: generateDiagramFlow } = useGenerateFlowService(
    async (res, error) => {
      setIsDiagramLoading(false);

      if (error) {
        if (error.name !== "AbortError") {
          toast.error(error.message || "Failed to generate diagram", {
            duration: TOAST_DURATION,
          });
        }
        return;
      }

      if (res?.metadata?.result && res?.metadata?.result?.length > 0) {
        const diagramComponents = res.metadata.result;
        await handleMissingDependencies(diagramComponents);
        const { svgContent, processedComponents } = compileDiagram(
          diagramComponents,
          canvasSize,
          shapesLibraryManager.getAllShapes(),
          showAttachmentPoints,
        );
        setContextDiagramComponents(processedComponents);
        setComposedSVG(svgContent);
        selectShape(null);
      } else {
        toast.error("No diagram components found", {
          duration: TOAST_DURATION,
        });
      }
    },
  );
  const [isPolling, setIsPolling] = useState(false);
  // Set of thread ids that are currently waiting on a response. Survives
  // explicit thread switches so if the user sends on A, jumps to B and sends,
  // then returns to A, both threads still show "Breeze.AI processing" until
  // each one's response actually lands.
  const [pendingThreadIds, setPendingThreadIds] = useState<Set<string>>(
    () => new Set(),
  );
  // The threadId (or undefined for a brand-new chat) that the in-flight
  // mutation originated from. Used to make the mutation-level `isPending`
  // busy indicator show only on the thread the user actually sent from —
  // otherwise switching threads mid-mutation makes the OTHER thread flash
  // "Breeze.AI processing" too. Ref because we only read it during render
  // (isPending / URL changes are what trigger re-render).
  const mutationSourceThreadRef = useRef<string | undefined | null>(null);
  // Thread id we just promoted to via `handleOnSendSettled` after a successful
  // New Chat send (URL upgraded from `/chat/:id` to `/chat/:id/t/:threadId`).
  // Two downstream effects normally wipe `messages` when `threadId` changes,
  // which briefly flashes the welcome screen even though the user's own bubble
  // is sitting in local state and a "Breeze.AI processing…" placeholder is
  // about to render. Recording the freshly-minted id here lets those effects
  // recognise the promotion and skip the wipe for exactly this transition.
  const promotedThreadRef = useRef<string | null>(null);
  const markThreadPending = useCallback((tid: string) => {
    setPendingThreadIds((prev) => {
      if (prev.has(tid)) return prev;
      const next = new Set(prev);
      next.add(tid);
      return next;
    });
  }, []);
  const clearThreadPending = useCallback((tid: string) => {
    setPendingThreadIds((prev) => {
      if (!prev.has(tid)) return prev;
      const next = new Set(prev);
      next.delete(tid);
      return next;
    });
  }, []);
  const [input, setInput] = useState("");
  // Deep-analysis toggle (general-query mode only) -> sends isDeepAnalysis to
  // backend/n8n. Persisted in localStorage so it survives nav away from the
  // Chat page (DEF-02) — the enclosing ChatLayout unmounts on route change,
  // so an in-component useState default would reset the toggle every time
  // the user came back from Dashboard/etc.
  //
  // Keyed by project id (`chat.deepAnalysis.{id}`) so each project remembers
  // its own preference — enabling deep analysis on project A must NOT flip
  // it on for project B.
  const deepAnalysisStorageKey = `chat.deepAnalysis.${id || "_"}`;
  const [isDeepAnalysis, setIsDeepAnalysis] = useState<boolean>(() => {
    try {
      return localStorage.getItem(deepAnalysisStorageKey) === "true";
    } catch {
      return false;
    }
  });
  // Re-hydrate on project switch (React Router keeps the Chat page mounted
  // when the `:id` param changes; without this the first project's toggle
  // would leak into the next project).
  const previousProjectIdRef = useRef(id);
  useEffect(() => {
    if (previousProjectIdRef.current === id) return;
    previousProjectIdRef.current = id;
    try {
      setIsDeepAnalysis(
        localStorage.getItem(`chat.deepAnalysis.${id || "_"}`) === "true",
      );
    } catch {
      setIsDeepAnalysis(false);
    }
  }, [id]);
  useEffect(() => {
    try {
      localStorage.setItem(
        deepAnalysisStorageKey,
        isDeepAnalysis ? "true" : "false",
      );
    } catch {
      // Ignore write errors (private mode, quota, etc.).
    }
  }, [deepAnalysisStorageKey, isDeepAnalysis]);
  const [diagramComponents, setDiagramComponents] = useState<
    DiagramComponent[]
  >([]);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [docInfo, setDocInfo] = useState({ docId: 0, enableRefetch: false });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // A question asked from the dashboard's Ask box arrives as router state. It goes into the input and, with
  // `autoSend`, is sent as soon as the page can send (the effect after isCurrentAgentBusy below), so the user
  // doesn't have to press Send again (BREEZEAI-1471 BUG-1). The state is cleared straight away, so a reload or
  // back-navigation neither fills nor sends it twice.
  const location = useLocation();
  const autoSendRef = useRef(false);
  useEffect(() => {
    const state = location.state as { prompt?: unknown; autoSend?: unknown } | null;
    const prompt = state?.prompt;
    if (typeof prompt !== "string" || !prompt.trim()) return;
    setInput(prompt.slice(0, MAX_CHAT_QUERY_LENGTH));
    autoSendRef.current = state?.autoSend === true;
    navigate(location.pathname, { replace: true, state: null });
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [location.state, location.pathname, navigate]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // Newest system-message _id that existed at the moment the user hit send.
  // Polling must continue until a system row NEWER than this appears completed,
  // so it can't stop on the previous (already-completed) answer.
  const baselineSystemIdRef = useRef<number>(0);
  // Has the "scroll to bottom on initial hydration" run for the current chat
  // context (project/agent/thread/chatMode)? Coordinates two scroll paths so
  // General Query mode — which hydrates a full cross-thread history on entry —
  // doesn't flicker between an initial paint at the top of the messages, a
  // delayed jump to the bottom, and a follow-up smooth scroll from the length-
  // change effect. Reset by the context-change effect below.
  const initialScrollDoneRef = useRef(false);
  // Previous `filteredMessages.length` at the time the smooth-scroll effect
  // last observed it. The effect only smooth-scrolls when the list actually
  // GROWS beyond this baseline, so:
  //   * Initial hydration (0 -> N) doesn't fire smooth on top of the pre-paint
  //     jump — the layout effect seeds this ref to N before this effect runs.
  //   * Mode filter changes that shrink the visible list don't trigger a
  //     scroll to bottom.
  const prevMessagesLengthRef = useRef(0);
  // Client-clock timestamp (ms) of when the current poll cycle started. Used for
  // the poll timeout instead of parsing server `createdAt`, whose timezone-naive
  // format the browser would misread as local time (a UTC value then looks hours
  // old, instantly tripping the 10-minute timeout and killing polling).
  const pollStartedAtRef = useRef<number>(0);
  // Tracks whether the user's scroll position is at/near the bottom of the chat.
  // Updated during scroll events (pre-DOM-update) so when new messages arrive or
  // data hydrates, we know if the user was reading at the bottom vs scrolled up.
  const isNearBottomRef = useRef(true);

  // Load older messages when user scrolls to top, and track near-bottom position
  const handleChatScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    isNearBottomRef.current = distanceFromBottom <= 120;

    if (container.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      const prevScrollHeight = container.scrollHeight;
      fetchNextPage().then(() => {
        // Preserve scroll position after prepending older messages
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight - prevScrollHeight;
          }
        });
      });
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isArchitectureAgent = agent === AGENTS.ARCHITECTURE_AGENT;

  // Poll for latest chats when processing (functional agent only).
  //
  // Only enable once `threadId` is defined — otherwise there's a brief window
  // right after send where `setIsPolling(true)` has run but the `navigate()`
  // URL update hasn't propagated to `useParams` yet. Firing the poll with
  // `threadId=undefined` in that window hits the backend with no thread
  // filter, which returns the newest 2 messages across ALL threads for this
  // user+project — usually the previous thread's already-completed exchange.
  // The poll effect would then see `systemChat.processingStatus === "completed"`
  // and stop polling before the new thread's response has even been inserted,
  // leaving "Breeze.AI processing" stuck forever with no response.
  // Threaded modes must have a threadId before polling (see the race note
  // above). General mode intentionally has none — it polls the pre-threads
  // way (no `threadId` query param), so we only require the flag there.
  const shouldPoll =
    isPolling &&
    !isArchitectureAgent &&
    (isGeneralMode || !!threadId);
  const { data: pollData } = usePollChatService(
    id,
    agent,
    shouldPoll,
    effectiveThreadId,
  );

  // Handle poll results: stop polling when system message is no longer processing
  // or when 10 minutes have passed since the user's last message
  const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  // Threaded modes fetch with a threadId query param, so every row returned
  // belongs to the current view. General mode is un-scoped (pre-threads flow)
  // and receives rows from EVERY thread on this project — including v2/v3
  // threads whose responses are still processing. Filter those out here so a
  // v3 thread's in-flight response doesn't drive general mode's polling /
  // busy-indicator state after the user has switched away from it. Rows
  // without a `chatMode` field are legacy general-mode rows.
  const isRowForCurrentContext = useCallback(
    (chat: Chat) => {
      if (!isGeneralMode) return true;
      return !chat.chatMode || chat.chatMode === ChatMode.GENERAL;
    },
    [isGeneralMode],
  );

  useEffect(() => {
    if (!pollData?.data || !isPolling) return;

    const systemChat = pollData.data.find(
      (chat) => chat.role === "system" && isRowForCurrentContext(chat),
    );

    // Timeout: stop polling 10 minutes after THIS poll cycle started. Measured
    // against the client clock (pollStartedAtRef) — NOT by parsing the server
    // `createdAt`, which arrives timezone-naive and was being misread as local
    // time, making a brand-new message look hours old and tripping this timeout
    // on the very first poll.
    if (pollStartedAtRef.current) {
      const elapsed = Date.now() - pollStartedAtRef.current;
      if (elapsed >= POLL_TIMEOUT_MS) {
        setIsPolling(false);
        if (threadId) clearThreadPending(threadId);
        refetch();
        return;
      }
    }

    // Keep polling if no system chat yet or if it's still processing
    if (!systemChat || systemChat.processingStatus === "processing") return;

    // Guard against matching a STALE previous system message. The agent inserts
    // the new answer row asynchronously, so an early poll tick can return the
    // PREVIOUS completed exchange. Only stop once a system row NEWER (higher
    // _id) than the one that existed at send-time appears — i.e. the actual
    // response to the latest query. Comparing user/system timestamps is not
    // enough: before the new user row is even inserted, both rows belong to the
    // old exchange (user older than system), which would stop polling early.
    if (systemChat._id != null && systemChat._id <= baselineSystemIdRef.current) {
      return;
    }

    // System message is done (completed/undefined with actual content)
    setIsPolling(false);
    if (threadId) clearThreadPending(threadId);
    refetch();
  }, [
    pollData,
    isPolling,
    refetch,
    threadId,
    clearThreadPending,
    isRowForCurrentContext,
  ]);

  // Clear rendered messages the instant we navigate to a different project,
  // agent, or thread (including the "no threadId" state that New Chat produces).
  // Without this the previous chat's bubbles linger until the new fetch resolves,
  // which makes thread-switch feel slow and makes New Chat look like it opened
  // the old conversation. The chatData -> setMessages effect below re-hydrates
  // as soon as the refetch lands. Polling / baseline / pollStartedAt reset is
  // handled inside handleSelectThread / handleNewChat so it doesn't fire when
  // handleOnSendSettled navigates from `no threadId` to the freshly-minted one.
  useEffect(() => {
    // Skip the wipe when this threadId change is the URL upgrade that
    // `handleOnSendSettled` just performed after a New Chat send — the user's
    // own message is already in local state and wiping it here would flash
    // the welcome screen for a beat before chatData for the new thread lands.
    if (promotedThreadRef.current && promotedThreadRef.current === threadId) {
      return;
    }
    setMessages([]);
  }, [id, agent, threadId, setMessages]);

  // On mount / refresh / thread-switch: reconcile pending state with what the
  // backend actually has. Two cases:
  //   1. The thread has a system row that's still `processing` → resume
  //      polling AND mark the thread pending (in case this was a fresh page
  //      load and pendingThreadIds is empty).
  //   2. The thread was marked pending but the fetched data already shows the
  //      response landed while the user was on another thread → clear the
  //      pending flag so the loading indicator stops.
  useEffect(() => {
    if (isArchitectureAgent || !chatData?.data?.length) return;

    // Scope reconciliation to rows belonging to the current view. In general
    // mode the fetch is un-scoped, so without this filter a v3 thread's still-
    // `processing` system row would (re)arm polling here immediately after the
    // user switched away from it, defeating the mode-change reset.
    const reversedData = [...chatData.data]
      .reverse()
      .filter(isRowForCurrentContext);
    const latestSystemChat = reversedData.find(
      (chat) => chat.role === "system",
    );
    const latestUserChat = reversedData.find((chat) => chat.role === "user");

    if (latestSystemChat?.processingStatus === "processing") {
      // Don't resume polling if the user's last message was sent more than 10
      // minutes ago. Parse the server timestamp as UTC (see parseServerUtcMs) —
      // a naive new Date() would misread it as local time and skip resuming.
      if (latestUserChat?.createdAt) {
        const elapsed = Date.now() - parseServerUtcMs(latestUserChat.createdAt);
        if (elapsed >= POLL_TIMEOUT_MS) return;
      }
      // Count the resume's 10-minute window from now, on the client clock.
      pollStartedAtRef.current = Date.now();
      setIsPolling(true);
      if (threadId) markThreadPending(threadId);
      return;
    }

    // Non-processing state with a system row newer than the user's row means
    // the response has landed for the currently visible thread. Belt-and-
    // suspenders for the single-threaded poll effect: if its baseline check
    // ever misses (e.g. pollData returned a stale system row and never got
    // past the `_id <= baseline` guard), `isPolling` would otherwise sit at
    // true forever with `refetch()` already having populated chatData with
    // the completed answer — leaving "Breeze.AI processing…" stuck below
    // the fully-rendered response.
    //
    // Only clear the global polling flag when the system row is genuinely
    // newer than the baseline captured at send time. A background React
    // Query refetch (window-focus, reconnect) that fires BEFORE the new
    // user message is committed would otherwise show only the previous
    // exchange (system newer than user) and stop polling by mistake.
    // (`processingStatus === "processing"` was handled by the early return
    // above; here it is guaranteed to be "completed" or undefined.)
    if (
      latestSystemChat &&
      latestUserChat &&
      latestSystemChat._id > latestUserChat._id
    ) {
      if (threadId && pendingThreadIds.has(threadId)) {
        clearThreadPending(threadId);
      }
      if (latestSystemChat._id > baselineSystemIdRef.current) {
        setIsPolling(false);
      }
    }
  }, [
    chatData,
    isArchitectureAgent,
    threadId,
    pendingThreadIds,
    markThreadPending,
    clearThreadPending,
    isRowForCurrentContext,
  ]);

  // isPending is a single mutation-level flag; scope it to the thread the
  // send came from so it doesn't leak into OTHER threads the user might jump
  // to while the request is still in flight.
  const mutationSourceMatchesThread =
    mutationSourceThreadRef.current !== null &&
    mutationSourceThreadRef.current === (threadId ?? undefined);
  const isCurrentThreadPending = !!threadId && pendingThreadIds.has(threadId);
  const isCurrentAgentBusy =
    (isPending && mutationSourceMatchesThread) ||
    isLoading ||
    isPolling ||
    isCurrentThreadPending;
  // Send the dashboard's question once it is in the input and the page is ready (history loaded, nothing in
  // flight). Submitting the form runs handleSend, the same path as clicking Send.
  useEffect(() => {
    if (!autoSendRef.current || !input.trim() || isCurrentAgentBusy) return;
    autoSendRef.current = false;
    formRef.current?.requestSubmit();
  }, [input, isCurrentAgentBusy]);
  // Same composition as the query handleSend builds, so the Send button's
  // disabled state agrees with what the submit handler will reject.
  const pendingQueryLength = [chatInput?.input?.trim(), input.trim()]
    .filter(Boolean)
    .join(" ").length;
  const isOverQueryLimit = pendingQueryLength > MAX_CHAT_QUERY_LENGTH;
  const isDesignAgent = agent === AGENTS.DESIGN_AGENT;
  const isChatAgent = agent === AGENTS.CHAT_AGENT;
  const isDesignModeInDesignAgent =
    isDesignAgent && chatMode === ChatMode.DESIGN;

  const handleCancelChatRequest = useCallback(() => {
    // Aborting the fetch only covers a send that has not been accepted yet;
    // once the server is working in the background it must be told to stop.
    abortSendMessage();
    setIsPolling(false);
    pollStartedAtRef.current = 0;
    if (threadId) {
      const stoppedThreadId = threadId;
      cancelRun(stoppedThreadId, {
        onSuccess: () => refetch(),
        onError: () => toast.error("Could not stop the response"),
        onSettled: () => clearThreadPending(stoppedThreadId),
      });
    }
    mutationSourceThreadRef.current = null;
  }, [abortSendMessage, cancelRun, clearThreadPending, refetch, threadId]);

  const handleOnMessageClick = async (message: Message) => {
    if (
      (message.metaData?.isGitQuery ||
        message.metaData?.outputFileType === "html" ||
        message.metaData?.outputFileType === "markdown") &&
      message.metaData?.documentId
    ) {
      setPanelProps({
        mode: "markdown",
        message: "processing",
      });
      setPanelType("MARKDOWN_RENDER");
      setDocInfo({ docId: message.metaData?.documentId, enableRefetch: true });
    } else if (message.metaData?.isGherkinScriptQuery) {
      setPanelProps({
        mode: "gherkin",
        message: message.text || "",
      });
      setPanelType("GHERKIN_SCRIPT");
    }
    //  else {
    //   setPanelProps({ mode: "diagram", uuid: id, messageId: message._id });
    //   setPanelType("DIAGRAM_TOOL");
    // }
  };

  // A fixed id keeps repeated keystrokes at the cap from stacking toasts.
  const notifyInputLimitReached = () =>
    toast.error(`Message limit of ${MAX_CHAT_QUERY_LENGTH} characters reached`, {
      id: "chat-input-limit",
      duration: TOAST_DURATION,
    });

  const handleSend = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isCurrentAgentBusy) return;

    const trimmedInput = input.trim();
    const trimmedChatInput = chatInput?.input?.trim() || "";

    if (!trimmedInput && !trimmedChatInput && !attachedFile) {
      toast.error("Please enter a message", {
        duration: TOAST_DURATION,
      });
      return;
    }

    const query = trimmedChatInput
      ? `${trimmedChatInput} ${trimmedInput}`
      : trimmedInput;

    if (query.length > MAX_CHAT_QUERY_LENGTH) {
      toast.error(`Message cannot exceed ${MAX_CHAT_QUERY_LENGTH} characters`, {
        duration: TOAST_DURATION,
      });
      return;
    }

    // Keep the optimistic bubble consistent with the protected request
    // payload. The service hook and backend apply the same defense too.
    const safeQuery = escapeChatQuery(query);

    const messageText = attachedFile
      ? safeQuery
        ? `${safeQuery}\n\nAttached: ${attachedFile.name}`
        : `Uploaded file: ${attachedFile.name}`
      : safeQuery;

    setMessages((prev) => [
      ...prev,
      {
        text: messageText,
        isUser: true,
        isSystemQuery: false,
        chatMode,
        createdAt: new Date().toISOString(),
        metaData: {},
      },
    ]);

    // Record the newest existing system-message id BEFORE sending. The agent
    // creates the answer row asynchronously, so the first poll can still return
    // the PREVIOUS exchange. Polling stops only once a system row newer than
    // this baseline appears completed (see the poll effect below).
    //
    // Baseline depends on the mode:
    //   * Threaded mode with a threadId → max system _id in the current
    //     thread's chatData.
    //   * Threaded mode with no threadId yet (New Chat) → 0. `chatData` here
    //     is the non-thread-scoped query and may include rows from OTHER
    //     threads; using its max would be a bogus ceiling the fresh thread
    //     can't clear, and polling would never stop.
    //   * General Query (pre-threads flow) → max system _id in the full
    //     cross-thread history. That IS the baseline the pre-threads
    //     implementation used, and it's what stops the poll effect from
    //     matching whatever old system row happens to be latest across
    //     threads and killing polling before n8n's response even lands.
    const useHistoryBaseline = isGeneralMode || !!threadId;
    baselineSystemIdRef.current = useHistoryBaseline
      ? (chatData?.data ?? []).reduce(
          (max, c) => (c.role === "system" && c._id && c._id > max ? c._id : max),
          0,
        )
      : 0;
    pollStartedAtRef.current = Date.now();
    // Remember which thread this mutation belongs to so the mutation-level
    // isPending indicator only shows on THAT thread. Existing thread → its
    // id. New Chat → undefined (we mark the freshly-minted id as pending
    // once the response returns).
    mutationSourceThreadRef.current = effectiveThreadId ?? undefined;
    // For existing threads we can mark pending immediately so the "Breeze.AI
    // processing" indicator survives an instant thread switch. New Chat can't
    // do this yet — the id doesn't exist until the backend mints one.
    // General Query has no thread at all, so nothing to mark.
    if (effectiveThreadId) markThreadPending(effectiveThreadId);

    mutate({
      agent,
      query: attachedFile ? `Uploaded file: ${attachedFile.name}` : safeQuery,
      uuid: id,
      currentState: diagramComponents,
      file: attachedFile || undefined,
      chatMode,
      isDeepAnalysis:
        chatMode === ChatMode.GENERAL || chatMode === ChatMode.GENERAL_V3
          ? isDeepAnalysis
          : false,
      // General Query is intentionally unthreaded — omit threadId so the
      // backend and n8n handle it in the pre-threads code path.
      threadId: effectiveThreadId,
    });
    setChatInput({ input: "", metadata: {} });
    setInput("");
    setAttachedFile(null);

    // Always scroll to bottom when user sends a message
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleGenerateMatrix = (message: Message, index: number) => {
    if (index === 0) return;
    const documentId =
      messages[index].metaData?.documentId ||
      messages[index - 1].metaData?.documentId;
    if (!documentId) return;

    const document = statusData?.find(
      (item) => item._id === Number(documentId),
    );

    // Determine metric type: only architecture or functional (default)
    const metricType =
      message.agent === AGENTS.ARCHITECTURE_AGENT
        ? "architecture"
        : "functional";

    // Select correct status field to check before triggering generation
    const metricStatusField = getDocumentOntologyStatus(document, metricType).status;

    if (metricStatusField === StatusEnum.done) {
      toast.success(
        `${
          metricType.charAt(0).toUpperCase() + metricType.slice(1)
        } metrics have already been generated for this document.`,
      );
      return;
    }
    startGenerationRun(
      // the chat's button asks for this one ontology (the impact agent would otherwise analyse every ontology)
      { projectUuid: id, documentId: Number(documentId), ontologies: [metricType], trigger: "chat" },
      {
        onSuccess: () => {
          toast.success("Generation started — you can review the changes when it finishes");
          queryClient.invalidateQueries({
            queryKey: ["document-by-id-with-filter", id],
          });
          queryClient.invalidateQueries({ queryKey: ["document-by-id"] });
          queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
        },
        onError: (error) => {
          toast.error((error as Error)?.message || "Failed to start generation");
        },
      },
    );
  };

  const handleGenerateDiagram = (_message: Message, index: number) => {
    if (index === 0) return;
    const documentId =
      messages[index].metaData?.documentId ||
      messages[index - 1].metaData?.documentId;
    if (!documentId) return;

    setIsDiagramLoading(true);
    generateDiagramFlow({
      uuid: id,
      key: "diagram",
      documentId: Number(documentId),
    });
  };

  const checkIfMetrixButtonDisabled = useMemo(
    () => (message: Message) => {
      if (!message.metaData?.uploadedType) return false;

      // Disable button when statusData is not yet loaded or empty
      if (!statusData || statusData.length === 0) return true;

      // find the corresponding document from statusData
      const doc = statusData?.find(
        (item) => item._id === Number(message?.metaData?.documentId),
      );
      if (!doc) return true; // Disable if document not found

      // choose which ontology's status to check based on agent type
      const { status } = getDocumentOntologyStatus(
        doc,
        message.agent === AGENTS.ARCHITECTURE_AGENT ? "architecture" : "functional",
      );

      return status === StatusEnum.inprogress || status === StatusEnum.done;
    },
    [statusData],
  );

  async function handleOnSendSettled(
    res: MessageResponse | undefined,
    error: Error | null,
  ) {
    // The mutation has resolved (success, failure, or abort). Detach the
    // "which thread this in-flight mutation belongs to" marker so `isPending`
    // stops decorating the send-time thread.
    const sourceThread = mutationSourceThreadRef.current;
    mutationSourceThreadRef.current = null;
    if (error) {
      if (error.name === "AbortError") return;
      // Send failed — the thread is no longer waiting on a response.
      if (typeof sourceThread === "string") clearThreadPending(sourceThread);
      // 409: an earlier answer on this thread is still being generated
      // (e.g. sent from another tab), so this message was not accepted.
      const isThreadBusy = (error as { status?: number }).status === 409;
      setMessages((prev) => [
        ...prev,
        {
          text: isThreadBusy ? THREAD_BUSY : WENT_WRONG,
          isUser: false,
          isSystemQuery: true,
          createdAt: new Date().toISOString(),
          metaData: {},
        },
      ]);
      return;
    }

    if (!res) return;

    // If the backend minted a fresh thread for this message (we sent no
    // threadId), reflect it in the URL so refresh / share / thread-switch
    // all target the same conversation. Also invalidate the sidebar list
    // so the new thread appears without waiting for a refetch interval.
    //
    // Skip this entirely in General Query mode — it must keep the pre-threads
    // URL shape (`/chat/{id}` with no `/t/{threadId}` segment) even if the
    // backend still returns a threadId for now.
    if (!isGeneralMode && res.threadId && res.threadId !== threadId) {
      queryClient.invalidateQueries({
        queryKey: chatThreadsQueryKey(id, agent),
      });
      // Flag the id BEFORE navigate so the downstream `threadId`-change and
      // chatData-hydration effects can tell "this transition is a New Chat
      // send just landing" apart from "user clicked a different thread" and
      // skip their `setMessages([])` wipe for this one transition.
      promotedThreadRef.current = res.threadId;
      navigate(buildChatPath(agent, id, res.threadId), { replace: true });
    }

    // Mark whichever thread this response belongs to as pending until polling
    // determines the system message is done. For an existing thread this is
    // idempotent (handleSend already marked it). For a New Chat send this is
    // the first opportunity to know the id.
    const effectivePendingThreadId = res.threadId || threadId;
    if (effectivePendingThreadId) markThreadPending(effectivePendingThreadId);

    // For non-architecture agents, start polling instead of showing the response immediately
    if (!isArchitectureAgent) {
      setIsPolling(true);
      // Invalidate document queries to refresh the attachment dropdown with newly uploaded files
      queryClient.invalidateQueries({
        queryKey: ["document-by-id", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["document-by-id-with-filter", id],
      });
      return;
    }

    // Architecture agent path is synchronous — the response is in `res`
    // directly, no polling needed, so clear pending immediately if we marked it.
    if (effectivePendingThreadId) clearThreadPending(effectivePendingThreadId);

    refetch();
    setDiagramComponents(res.metadata.content ?? []);

    const action = res.metadata?.action?.[0]?.action;
    const newContent = res.metadata.content ?? [];

    const actionHandlers: Record<string, () => void> = {
      undo: () => {
        const previousState = history.undo();
        if (previousState) setContextDiagramComponents(previousState);
      },
      redo: () => {
        const nextState = history.redo();
        if (nextState) setContextDiagramComponents(nextState);
      },
    };

    const handler = action && actionHandlers[action];
    if (handler) {
      handler();
    } else {
      const hasContentChanged = !equal(newContent, diagramComponents);
      if (hasContentChanged) {
        // Fetch and register any missing components/shapes before setting diagram
        await handleMissingDependencies(newContent);
        setContextDiagramComponents(newContent);
        history.addHistory(newContent);

        // Trigger fitView after diagram update
        window.dispatchEvent(new CustomEvent("triggerFitView"));
      }
    }
  }
  async function getDocumentContent(documentId: number) {
    const headers = getAuthHeaders();
    const gitDocument = await queryClient.fetchQuery({
      queryKey: ["git-document-by-id", documentId],
      queryFn: () => getGitDocumentById(documentId, headers),
    });

    setPanelProps({
      mode: "markdown",
      message:
        gitDocument.data.status === "processing"
          ? "processing"
          : gitDocument.data.content,
    });

    if (gitDocument.data.status === "active") {
      setDocInfo({
        docId: 0,
        enableRefetch: false,
      });
    }
  }

  const scrollToBottom = () => {
    isNearBottomRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Matched on MIME or extension — Windows reports no usable MIME for
    // .md/.txt/Office files, so a MIME-only check rejects valid uploads.
    if (!isValidFileType(file)) {
      toast.error(
        `Unsupported file type. Allowed formats: ${SUPPORTED_FORMATS_SHORT}.`,
        {
          duration: TOAST_DURATION,
        },
      );
      event.target.value = "";
      return;
    }

    // Store the file in state instead of immediately sending
    setAttachedFile(file);
    setIsDropdownOpen(false);

    // Reset file input to allow re-selecting the same file
    event.target.value = "";
  };

  const handleCancelApiAndClose = () => {
    // Dispatch abort events for both blueprint and diagram generation
    window.dispatchEvent(new CustomEvent("abortBlueprintGeneration"));
    window.dispatchEvent(new CustomEvent("abortDiagramGeneration"));
  };

  // Filter messages based on the currently selected chatMode
  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      // If message doesn't have chatMode (old messages), show them only in general mode
      if (!message.chatMode) return chatMode === ChatMode.GENERAL;
      // Only show messages that match the current chatMode
      return message.chatMode === chatMode;
    });
  }, [messages, chatMode]);

  // ---- Raise Jira Ticket -------------------------------------------------
  // The board list doubles as the capability probe: empty (or a 403 for a
  // Viewer) hides the button entirely, which is AC 6 without a second request.
  const { data: jiraBoards = [] } = useListJiraBoards(id || undefined);
  const canRaiseJira = jiraBoards.length > 0;

  const [jiraDialog, setJiraDialog] = useState<{
    open: boolean;
    messages: ChatTranscriptTurn[];
    fallbackText: string;
  }>({ open: false, messages: [], fallbackText: "" });

  /**
   * Builds the transcript slice for the drafting agent.
   *
   * The client does this rather than the server because in general chat mode
   * there is no conversation boundary to query by: threadId is undefined by
   * design and `chats.uuid` is the *project* uuid, so a "last N rows for this
   * user" query would happily staple yesterday's unrelated question onto today's
   * diagnosis. `filteredMessages` is already exactly the conversation on screen.
   */
  const buildJiraContext = (clickedIndex: number): ChatTranscriptTurn[] => {
    const upToClicked = filteredMessages.slice(0, clickedIndex + 1);

    // Walk backwards counting user turns, so "5 pairs" means five exchanges
    // rather than five messages, and each user turn keeps its assistant reply.
    const collected: Message[] = [];
    let userTurns = 0;
    for (let i = upToClicked.length - 1; i >= 0; i--) {
      const message = upToClicked[i];
      if (message.isUser) {
        if (userTurns >= JIRA_CONTEXT_PAIRS) break;
        userTurns++;
      }
      collected.push(message);
    }
    collected.reverse();

    const turns = collected
      .map((message) => ({
        role: message.isUser ? ("user" as const) : ("assistant" as const),
        text: stripBreezeAiFences(message.text),
      }))
      .filter((turn) => turn.text !== "")
      .slice(-JIRA_MAX_TURNS)
      .map((turn) => ({ ...turn, text: turn.text.slice(0, JIRA_MAX_TURN_CHARS) }));

    // Drop oldest turns until the combined text fits. Oldest first because the
    // recent turns hold the conclusion the ticket is actually about.
    while (
      turns.length > 1 &&
      turns.reduce((sum, turn) => sum + turn.text.length, 0) > JIRA_MAX_TOTAL_CHARS
    ) {
      turns.shift();
    }
    // A single very long answer can still exceed the total on its own.
    if (turns.length === 1) {
      turns[0].text = turns[0].text.slice(0, JIRA_MAX_TOTAL_CHARS);
    }

    return turns;
  };

  // Puts the question that got no answer back in the input box so the user can
  // resend it as-is or adjust it first.
  const handleRetry = (index: number) => {
    for (let i = index - 1; i >= 0; i--) {
      if (filteredMessages[i].isUser) {
        setInput(filteredMessages[i].text);
        inputRef.current?.focus();
        return;
      }
    }
  };

  const handleRaiseJiraTicket = (message: Message, index: number) => {
    setJiraDialog({
      open: true,
      messages: buildJiraContext(index),
      fallbackText: stripBreezeAiFences(message.text),
    });
  };

  // Whenever the user navigates to a different conversation, the next
  // hydration needs to do its own instant-scroll-to-bottom again.
  useEffect(() => {
    initialScrollDoneRef.current = false;
    prevMessagesLengthRef.current = 0;
    isNearBottomRef.current = true;
  }, [id, agent, threadId, chatMode]);

  // Pre-paint scroll for hydration and subsequent message arrivals / AI thinking
  // transitions:
  // - On initial hydration (thread switch / navigation), jumps to bottom pre-paint.
  // - On new messages or AI thinking status change, scrolls to bottom if user is at bottom.
  // - Runs a short settling window that re-anchors to bottom while markdown, code
  //   highlighting, images, or async components reflow, stopping immediately if the
  //   user manually scrolls away.
  useLayoutEffect(() => {
    if (filteredMessages.length === 0) return;
    const container = chatContainerRef.current;
    if (!container) return;

    const isInitial = !initialScrollDoneRef.current;
    const shouldScroll = isInitial || isNearBottomRef.current;

    if (shouldScroll) {
      container.scrollTop = container.scrollHeight;
      initialScrollDoneRef.current = true;
      prevMessagesLengthRef.current = filteredMessages.length;

      let cancelled = false;
      const stopAt = performance.now() + 600;
      const pin = () => {
        if (cancelled || !isNearBottomRef.current) return;
        if (!chatContainerRef.current) return;
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
        if (performance.now() < stopAt) requestAnimationFrame(pin);
      };
      requestAnimationFrame(pin);

      return () => {
        cancelled = true;
      };
    } else {
      prevMessagesLengthRef.current = filteredMessages.length;
    }
  }, [filteredMessages, isCurrentAgentBusy]);

  useEffect(() => {
    // In threaded modes, "no threadId in URL" means the user is on the
    // "New Chat" landing — GET /chat/byUUID would return the project's whole
    // history, but the UX intent is a fresh empty panel. Skip hydration in
    // that case.
    //
    // General Query mode is the exception: it pre-dates threads and DOES use
    // the whole /chat/byUUID stream as its conversation. Always hydrate.
    if (!isGeneralMode && !threadId) {
      setMessages([]);
      return;
    }
    // Right after a New Chat send, `threadId` is freshly-minted but the
    // per-thread chatData query hasn't refetched yet (it's still undefined).
    // Wiping to `[]` in that window would replace the user's own message with
    // the welcome screen for a beat. Wait for chatData to hydrate; the effect
    // will re-run once it arrives.
    if (
      promotedThreadRef.current &&
      promotedThreadRef.current === threadId &&
      !chatData?.data?.length
    ) {
      return;
    }
    const existingMessages = chatData?.data
      .filter((chat) => {
        // Hide system messages that are still processing with empty/placeholder content
        if (
          chat.role === "system" &&
          chat.processingStatus === "processing" &&
          (!chat.message?.trim() || chat.message?.trim() === "''")
        ) {
          return false;
        }
        return true;
      })
      .map((chat, index, arr) => {
        // System messages may not have chatMode from the backend.
        // Inherit from the preceding user message so they appear in the
        // correct mode tab instead of only showing in "general" mode.
        let resolvedChatMode = chat.chatMode;
        if (!resolvedChatMode && chat.role === "system") {
          for (let i = index - 1; i >= 0; i--) {
            if (arr[i].role === "user" && arr[i].chatMode) {
              resolvedChatMode = arr[i].chatMode;
              break;
            }
          }
        }
        // For system messages, prefer `updatedAt` — the row is INSERTed at
        // send time (with placeholder text) and later UPDATEd with the LLM's
        // answer, so `updatedAt` reflects when the response actually arrived.
        // User rows are never updated, so `createdAt` is correct there.
        const displayTimestamp =
          chat.role === "system"
            ? chat.updatedAt || chat.createdAt
            : chat.createdAt;
        const existingMessage: Message = {
          _id: chat._id,
          text: chat.message,
          agent: chat.agent,
          isUser: chat.role === "user",
          isSystemQuery: chat.role === "system",
          processingStatus: chat.processingStatus,
          chatMode: resolvedChatMode,
          createdAt: displayTimestamp,
          sources: chat.sources ?? null,
          metaData: chat.metadata ?? {},
        };
        return existingMessage;
      });
    setMessages(existingMessages || []);
    // Successful hydration for the promoted thread — release the flag so the
    // NEXT thread-switch (whether it's a New Chat or a click on another
    // thread in the sidebar) goes through its normal wipe path.
    if (
      promotedThreadRef.current &&
      promotedThreadRef.current === threadId &&
      existingMessages &&
      existingMessages.length > 0
    ) {
      promotedThreadRef.current = null;
    }

    // Initial scroll-to-bottom on hydration is handled by the useLayoutEffect
    // above (pre-paint) — no delayed setTimeout scroll here, which used to
    // cause a visible top-of-messages flash followed by a jump in General
    // Query mode.
  }, [chatData, threadId, isGeneralMode, setMessages]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (!docInfo.enableRefetch || interval) {
      clearInterval(interval);
      return;
    }
    interval = setInterval(() => {
      getDocumentContent(docInfo.docId);
    }, REFETCH_INTERVAL);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [docInfo.enableRefetch]);

  // Switching TO General Query while the URL still carries a `/t/{threadId}`
  // (e.g. because the user was previously in v2) leaves a stale thread
  // segment in the address bar. Drop it — General Query's URL shape is
  // `/chat/{id}` to match production and the pre-threads UX.
  useEffect(() => {
    if (isGeneralMode && threadId) {
      navigate(buildChatPath(agent, id), { replace: true });
    }
  }, [isGeneralMode, threadId, agent, id, navigate]);

  // Welcome-screen visibility. Suppress it when the user has actually
  // selected a thread but its data hasn't landed yet — otherwise the
  // "BREEZE.AI Assistant" landing flashes on top of the empty content area
  // for the duration of the fetch, right before the real messages render.
  // Rules:
  //   * `showWelcomeScreen` — user toggled it on explicitly via a button.
  //   * Empty message list AND either
  //       (a) general mode (no threading at all — welcome IS the empty
  //           state), or
  //       (b) threaded mode with no threadId in the URL (the "New Chat"
  //           landing — welcome is the intended empty state).
  //   * Threaded mode with a threadId whose data is still loading — show
  //     nothing (blank pane) instead of flashing the welcome.
  const shouldShowWelcome =
    showWelcomeScreen ||
    (filteredMessages.length === 0 && (isGeneralMode || !threadId));

  // Reset the SINGLE-THREADED polling machinery (isPolling / baselineSystemIdRef
  // / pollStartedAtRef) when the user leaves the current thread. Those refs
  // belong to the thread that was active when send was pressed. Defined here,
  // above the `Chat` JSX, because both the deep-analysis toggle handler below
  // and the thread select / new-chat handlers (further down) call it.
  // `pendingThreadIds` is deliberately NOT reset here (see the thread handlers).
  const resetPollingState = useCallback(() => {
    setIsPolling(false);
    baselineSystemIdRef.current = 0;
    pollStartedAtRef.current = 0;
  }, []);

  // Flipping the deep-analysis toggle. In threaded general_v3, deep-analysis and
  // regular chats live in SEPARATE threads, so the toggle also chooses which
  // thread list you're in: reset the active thread and land on a fresh New Chat
  // in that mode. The sidebar (filtered by isDeepAnalysis) then shows the
  // matching threads and the conversation panel clears. General mode (n8n) has
  // no threads, so there we only flip the flag.
  const handleToggleDeepAnalysis = useCallback(
    (checked: boolean) => {
      setIsDeepAnalysis(checked);
      if (chatMode === ChatMode.GENERAL_V3) {
        resetPollingState();
        navigate(buildChatPath(agent, id));
      }
    },
    [agent, id, navigate, resetPollingState, chatMode],
  );

  // Switching chat modes must reset the same single-threaded polling machinery
  // that thread-switch / new-chat / deep-analysis-toggle already reset. Without
  // this, an in-flight response from the previous mode leaves `isPolling` true,
  // so `AiThinking` keeps rendering "Breeze.AI processing…" on the new mode's
  // view even though nothing there is loading. The in-flight mutation is left
  // alone on purpose — the server-side commit still lands, and returning to
  // the source thread re-arms polling via the mount-time resume effect.
  const handleChatModeChange = useCallback(
    (nextMode: ChatMode) => {
      if (nextMode === chatMode) return;
      resetPollingState();
      setChatMode(nextMode);
    },
    [chatMode, resetPollingState, setChatMode],
  );

  // Auto-open design agent panel when in design agent chat.
  // NOTE: Rendered as plain JSX (not memoized) so the UI always reflects the
  // latest state. A previous React.useMemo here required a hand-maintained
  // dependency array; omitting any state used inside (e.g. isDeepAnalysis)
  // caused stale renders where toggles/buttons appeared unclickable.
  const Chat = (
    <>
      <div
        ref={chatContainerRef}
        onScroll={handleChatScroll}
        className={`flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 px-4 py-3 min-h-0 ${CUSTOM_SCROLLBAR}`}
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
        aria-relevant="additions"
      >
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-5 h-5 animate-spin text-muted" />
          </div>
        )}
        {/* Welcome screens only render on the true "no conversation selected"
            state — see the `shouldShowWelcome` derivation above. */}
        {isChatAgent && shouldShowWelcome ? (
          <ChatWelcomeScreen
            onQuerySelect={(query) => {
              setInput(query);
              setShowWelcomeScreen(false);
              inputRef.current?.focus();
            }}
            onDismiss={
              filteredMessages.length > 0
                ? () => setShowWelcomeScreen(false)
                : undefined
            }
            hasExistingMessages={filteredMessages.length > 0}
            chatMode={chatMode}
          />
        ) : isArchitectureAgent && shouldShowWelcome ? (
          <ArchitectureWelcomeScreen
            onQuerySelect={(query) => {
              setInput(query);
              setShowWelcomeScreen(false);
              inputRef.current?.focus();
            }}
            onDismiss={
              filteredMessages.length > 0
                ? () => setShowWelcomeScreen(false)
                : undefined
            }
            hasExistingMessages={filteredMessages.length > 0}
          />
        ) : (
          <>
            {/* Show default agent description for non-chat/architecture agents */}
            {!filteredMessages.length &&
              !isChatAgent &&
              !isArchitectureAgent && (
                <MessageLayout
                  key={"agent description"}
                  index={0}
                  message={{
                    ...MESSAGE_SHELL,
                    text: AGENT_DESCRIPTIONS[agent as AGENTS],
                  }}
                />
              )}

            {filteredMessages.map((message: Message, idx) => (
              <MessageLayout
                key={idx}
                index={idx}
                message={message}
                handleOnClick={handleOnMessageClick}
                handleGenerateMatrix={handleGenerateMatrix}
                handleGenerateDiagram={handleGenerateDiagram}
                isMatrixButtonDisabled={checkIfMetrixButtonDisabled(message)}
                canRaiseJira={canRaiseJira}
                onRaiseJiraTicket={handleRaiseJiraTicket}
                onRetry={handleRetry}
                canViewRunDetails={!!chatData?.canViewRunDetails}
              />
            ))}

            <AiThinking isLoading={isCurrentAgentBusy} />
          </>
        )}

        <div ref={messagesEndRef} aria-hidden="true" />
      </div>

      <form
        onSubmit={handleSend}
        ref={formRef}
        className="bg-surface-card shadow-lg rounded-2xl p-3 flex-shrink-0 mx-4 mb-3"
        aria-label="Chat message input"
      >
        {chatInput.input && (
          <div className="flex items-center mb-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary relative">
            <span className="flex-1 text-sm font-medium">
              {chatInput.input}
            </span>
            <button
              type="button"
              className="ml-3 p-1.5 rounded-md hover:bg-primary/20 transition-colors"
              onClick={() => setChatInput({ input: "", metadata: {} })}
              tabIndex={-1}
              aria-label="Clear preset message"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {attachedFile && (
          <div className="flex items-center mb-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-success relative">
            <span className="flex-1 text-sm font-medium">
              📎 {attachedFile.name}
            </span>
            <button
              type="button"
              className="ml-3 p-1.5 rounded-md hover:bg-success/20 transition-colors"
              onClick={() => setAttachedFile(null)}
              tabIndex={-1}
              aria-label="Remove attached file"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="relative w-full grow items-center overflow-hidden bg-surface-input rounded-xl border border-surface-border/30 focus-within:border-primary/40 transition-colors">
          <label htmlFor="messageInput" className="sr-only">
            Type your message
          </label>
          <Textarea
            id="messageInput"
            ref={inputRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            placeholder={CHAT_MODES[chatMode].placeholder}
            className="text-white min-h-[48px] w-full resize-none bg-transparent px-3 py-3 focus-within:outline-none sm:text-sm scrollbar-hide placeholder-muted"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            name="message"
            rows={1}
            maxLength={MAX_CHAT_QUERY_LENGTH}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // A paste longer than the cap is cut by the browser; say so.
              if (e.target.value.length >= MAX_CHAT_QUERY_LENGTH) {
                notifyInputLimitReached();
              }
            }}
            onBeforeInput={(e) => {
              // The browser silently drops typing past maxLength; say so.
              const el = e.currentTarget;
              if (
                input.length >= MAX_CHAT_QUERY_LENGTH &&
                el.selectionStart === el.selectionEnd
              ) {
                notifyInputLimitReached();
              }
            }}
            aria-describedby="chat-input-hint"
          />
          {input.length > 0 && (
            <div
              className={cn(
                "px-3 pb-1.5 text-right text-xs tabular-nums",
                input.length >= MAX_CHAT_QUERY_LENGTH
                  ? "text-error"
                  : "text-muted",
              )}
            >
              {input.length} / {MAX_CHAT_QUERY_LENGTH}
            </div>
          )}
        </div>
        <div id="chat-input-hint" className="sr-only">
          Press Enter to send your message, or Shift+Enter for a new line.
        </div>
        <div className="flex gap-2 justify-between items-center mt-2 pt-2 border-t border-surface-border/30">
          <div className="flex gap-2 items-center">
            {/* Hide ChatModeSelector for architecture agent */}
            {!isArchitectureAgent && (
              <ChatModeSelector
                selectedMode={chatMode}
                onModeChange={handleChatModeChange}
                currentAgent={agent}
              />
            )}
            {/* Deep Analysis toggle — General Query mode (v1 n8n or v3 Python) */}
            {!isArchitectureAgent &&
              (chatMode === ChatMode.GENERAL ||
                chatMode === ChatMode.GENERAL_V3) && (
                <>
                  <span
                    className="h-5 w-px bg-surface-border/60 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="flex items-center gap-2 px-1.5 select-none whitespace-nowrap">
                    <Switch
                      type="button"
                      id="deep-analysis-toggle"
                      checked={isDeepAnalysis}
                      onCheckedChange={handleToggleDeepAnalysis}
                      aria-label="Toggle deep analysis"
                    />
                    <label
                      htmlFor="deep-analysis-toggle"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Deep analysis
                    </label>
                  </div>
                </>
              )}
            {/* Show Examples button for chat/architecture agent when there are messages */}
            {(isChatAgent || isArchitectureAgent) &&
              filteredMessages.length > 0 &&
              !showWelcomeScreen && (
                <Button
                  type="button"
                  onClick={() => setShowWelcomeScreen(true)}
                  variant="outline"
                  size="sm"
                  className="border-primary/40 text-primary hover:bg-primary hover:text-white transition-colors"
                >
                  <Lightbulb className="w-4 h-4 mr-1" />
                  Examples
                </Button>
              )}
          </div>
          <div className="flex gap-2 items-center">
            {isArchitectureAgent &&
              (isDesignModeInDesignAgent ? (
                <button
                  className="h-6 w-6 flex items-center justify-center opacity-50 cursor-not-allowed"
                  onClick={() => {
                    toast.error("File upload during design mode not supported");
                  }}
                  aria-label="Attach file (disabled in design mode)"
                  aria-disabled="true"
                >
                  <CirclePlus aria-hidden="true" />
                </button>
              ) : (
                <AttachmentLayout
                  trigger={
                    <button
                      className="h-6 w-6 flex items-center justify-center"
                      aria-label="Attach file or link"
                    >
                      <CirclePlus
                        className="cursor-pointer"
                        aria-hidden="true"
                      />
                    </button>
                  }
                  dropdownControls={{
                    isDropdownOpen,
                    setIsDropdownOpen,
                  }}
                  handleUploadBoxOpen={() => fileInputRef.current?.click()}
                  hideGenerateOptions={false}
                />
              ))}
            {isCurrentAgentBusy ? (
              <button
                key="stop-button"
                className="h-6 w-6 flex items-center justify-center rounded-full bg-primary text-button-text hover:bg-primary-dark transition-colors"
                type="button"
                onClick={handleCancelChatRequest}
                aria-label="Stop generating"
                title="Stop generating"
              >
                <Square className="h-3 w-3 fill-current" />
                <span className="sr-only">Stop generating</span>
              </button>
            ) : (
              <button
                key="send-button"
                className="h-6 w-6 flex items-center justify-center disabled:opacity-50"
                type="submit"
                aria-label="Send message"
                aria-disabled={isOverQueryLimit || undefined}
                title="Send message"
              >
                <SendIcon />
                <span className="sr-only">Send message</span>
              </button>
            )}
          </div>
        </div>
      </form>
    </>
  );
  // resetPollingState is defined above the `Chat` JSX (the deep-analysis toggle
  // needs it too). handleSelectThread / handleNewChat still change threadId via
  // navigate(); handleOnSendSettled's own navigate() (undefined → freshly-minted
  // id) must NOT clear the polling flag it just set, which is why the reset is
  // called explicitly from these handlers rather than a threadId-change effect.
  const handleSelectThread = useCallback(
    (nextThreadId: string) => {
      if (nextThreadId === threadId) return;
      resetPollingState();
      navigate(buildChatPath(agent, id, nextThreadId));
    },
    [agent, id, navigate, resetPollingState, threadId],
  );

  const handleNewChat = useCallback(() => {
    // Drop the threadId segment — the next message the user sends will
    // cause the backend to mint a fresh thread and we'll push its id back
    // into the URL from handleOnSendSettled.
    resetPollingState();
    navigate(buildChatPath(agent, id));
  }, [agent, id, navigate, resetPollingState]);

  return (
    <div className="flex-1 flex justify-center">
      <div className="w-full h-screen flex flex-col text-white bg-background overflow-hidden">
        <div className="pt-4 flex-shrink-0">
          <BreadCrumb />
        </div>

        <div className="flex flex-1 min-h-0 pt-11">
          {/*
            Threads sidebar is a General-Query-v2 (and future modes) feature.
            The original General Query mode pre-dates threads — leave it with
            its original single-stream UX by not rendering the sidebar there.
          */}
          {!isArchitectureAgent && chatMode !== ChatMode.GENERAL && (
            <ChatThreadsSidebar
              uuid={id}
              agent={agent}
              chatMode={chatMode}
              // Deep analysis only branches the thread list in general_v3; other
              // threaded modes have no deep-analysis threads, so pass false.
              isDeepAnalysis={
                chatMode === ChatMode.GENERAL_V3 ? isDeepAnalysis : false
              }
              activeThreadId={threadId}
              onSelectThread={handleSelectThread}
              onNewChat={handleNewChat}
            />
          )}
          <div className="flex flex-col flex-1 min-h-0 items-center">
            <section
              className={cn(
                "flex flex-col flex-1 w-full min-h-0",
                !isArchitectureAgent && "max-w-screen-lg",
              )}
            >
              {isArchitectureAgent ? (
                <ArchitectureAgentLayout Chat={Chat} />
              ) : (
                Chat
              )}
            </section>
          </div>
        </div>

        {/* Hidden file input for file uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT_ATTR}
          onChange={handleFileChange}
          className="hidden"
          aria-hidden="true"
        />

        {/* Diagram Loading Dialog */}
        <Dialog open={isDiagramLoading} onOpenChange={handleCancelApiAndClose}>
          <DialogContent className="bg-surface border-surface-border text-white max-w-md">
            <DialogHeader className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Layers className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold mb-2">
                  Loading Diagram
                </DialogTitle>
                <DialogDescription className="text-muted text-sm">
                  {currentDiagramInfo?.metadata?.description && (
                    <span className="block font-medium text-white mb-1">
                      {currentDiagramInfo.metadata.description}
                    </span>
                  )}
                  Processing components and dependencies...
                </DialogDescription>
              </div>
            </DialogHeader>
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm text-muted">Please wait</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mounted once at layout level, not per message: one dialog driven by
            the slice captured when the button was clicked. */}
        <RaiseJiraTicketDialog
          open={jiraDialog.open}
          onOpenChange={(open) => setJiraDialog((prev) => ({ ...prev, open }))}
          projectUuid={id}
          messages={jiraDialog.messages}
          fallbackText={jiraDialog.fallbackText}
        />
      </div>
    </div>
  );
};
export default ChatScreen;
